/**
 * King of the Hill and Volleyball. The host simulates; guests copy the score
 * and life labels out of the snapshot and draw them.
 */
import { GameBehaviour, type GamePlayer } from '../game.ts';
import type { Collision2D, SimGo } from '../world.ts';
import { FrameCounter, type RGBA, type V2 } from '../unity.ts';
import { PlayerController, PlayerState } from '../player.ts';
import { DynamicObject } from './dynamic.ts';
import { playerOf } from './util.ts';

const RESPAWN_FRAMES = 120 * 4;
const DEAD_TEXT = ['dEaD', 'Dead', 'Dead', 'Dead', 'Dead', 'Dead', 'DeD', 'Obliterated', 'Deceased', 'RIP', 'Did you even try?'];

export interface ModeLabel {
  x: number;
  y: number;
  text: string;
  color: RGBA;
}

export interface KothRow {
  slot: number;
  lives: number;
  text: string;
  color: [number, number, number];
}

class KothPlayer {
  lives = 5;
  text = '5';
  respawn = new FrameCounter(RESPAWN_FRAMES);
  constructor(readonly player: PlayerController) {}
}

/** Five lives, a four-second respawn, then permadeath. Deaths do not reload the checkpoint. */
export class KOTHHandler extends GameBehaviour {
  static override checkpointable = false;
  private players: KothPlayer[] = [];
  labels: ModeLabel[] = [];
  private onDied = (p: GamePlayer) => this.died(p);

  override start() {
    this.game.onPlayerDied.push(this.onDied);
    this.ensurePlayers();
  }

  /** KOTHHandler.ShouldStartCheckpointLoadCounter returns false. */
  blocksCheckpointLoad(): boolean {
    return true;
  }

  override fixedUpdate() {
    this.ensurePlayers();
    for (const row of this.players) {
      if (row.respawn.countCheckFinishReset()) row.player.respawnFresh(this.game.getRespawnPoint(row.player), true);
    }
    this.layout();
  }

  override onDestroy() {
    this.game.onPlayerDied = this.game.onPlayerDied.filter((fn) => fn !== this.onDied);
  }

  rows(): KothRow[] {
    return this.players.map((row) => ({
      slot: row.player.index,
      lives: row.lives,
      text: row.text,
      color: [row.player.color[0], row.player.color[1], row.player.color[2]],
    }));
  }

  /** Guest: the host already resolved lives. `slot` is the player index in this level. */
  applyNet(rows: KothRow[] | undefined) {
    if (!rows) return;
    this.labels = rows.map((row, i) => ({
      text: row.text,
      color: [row.color[0], row.color[1], row.color[2], 1],
      ...this.linePos(i),
    }));
    for (const row of rows) {
      const player = this.game.players[row.slot];
      if (player instanceof PlayerController) player.livesLabel = row.text;
    }
  }

  private ensurePlayers() {
    for (const p of this.game.players) {
      if (!(p instanceof PlayerController) || p.go.destroyed) continue;
      if (this.players.some((row) => row.player === p)) continue;
      const row = new KothPlayer(p);
      row.text = '5';
      p.livesLabel = '5';
      this.players.push(row);
    }
    this.layout();
  }

  private died(p: GamePlayer) {
    const row = this.players.find((item) => item.player === p);
    if (!row || !(p instanceof PlayerController)) return;
    if (row.lives > 0) {
      row.lives -= 1;
      row.text = String(row.lives);
      p.livesLabel = row.text;
      row.respawn.restart();
      this.sound('kothPoint', p.go.position);
    } else {
      row.lives = 0;
      row.text = DEAD_TEXT[Math.floor(this.game.random() * DEAD_TEXT.length)] ?? 'Dead';
      p.livesLabel = row.text;
      p.playerState = PlayerState.Permadead;
      this.sound('kothOut', p.go.position);
      const alive = this.players.filter((item) => item.player.playerState !== PlayerState.Permadead);
      if (this.players.length > 1 && alive.length === 1) this.sound('winner', alive[0].player.go.position);
    }
    this.layout();
  }

  private linePos(i: number): V2 {
    const p = this.go.position;
    return { x: p.x + 3.5, y: p.y - 0.7 * i };
  }

  private layout() {
    this.labels = this.players.map((row, i) => ({
      text: row.text,
      color: [row.player.color[0], row.player.color[1], row.player.color[2], 1] as RGBA,
      ...this.linePos(i),
    }));
  }
}

export interface VolleySnap {
  left: number;
  right: number;
  color?: [number, number, number, number];
}

export const enum VolleyballState {
  Start,
  Volleying,
  Serving,
}

/** The net. Scoring is on the ball; this holds the two numbers and where to draw them. */
export class VolleyballHandler extends GameBehaviour {
  static override checkpointable = false;
  leftScore = 0;
  rightScore = 0;
  labels: ModeLabel[] = [];
  private leftGo: SimGo | null = null;
  private rightGo: SimGo | null = null;

  override start() {
    this.leftGo = this.refGo(this.data.leftScoreTMP);
    this.rightGo = this.refGo(this.data.rightScoreTMP);
    this.layout();
    this.game.volleyHud = { left: this.leftScore, right: this.rightScore };
  }

  logGoal(side: number) {
    if (side === -1) this.rightScore++;
    else this.leftScore++;
    this.layout();
    this.game.volleyHud = { left: this.leftScore, right: this.rightScore };
  }

  applyNet(snap: VolleySnap | undefined) {
    if (!snap) return;
    this.leftScore = snap.left;
    this.rightScore = snap.right;
    this.layout();
    this.game.volleyHud = { left: this.leftScore, right: this.rightScore };
  }

  private layout() {
    const o = this.go.position;
    const left = this.leftGo?.position ?? { x: o.x - 2, y: o.y + 2 };
    const right = this.rightGo?.position ?? { x: o.x + 2, y: o.y + 2 };
    const color: RGBA = [1, 1, 1, 1];
    this.labels = [
      { x: left.x, y: left.y, text: String(this.leftScore), color },
      { x: right.x, y: right.y, text: String(this.rightScore), color },
    ];
  }
}

/** The ball. Floor contact on a side that is in play scores for the other side. */
export class Volleyball extends GameBehaviour {
  static override checkpointable = false;
  baseColor: RGBA = [1, 1, 1, 1];
  isAtBaseColor = true;
  ballSide = 0;
  ballOnFloorCounter = 0;
  state = VolleyballState.Start;
  private dynamic: DynamicObject | null = null;

  override start() {
    const src = this.refGo(this.data.spriteRendererForBaseColor);
    const c = src?.sprite?.color;
    if (c) this.baseColor = [c[0], c[1], c[2], c[3]];
    this.dynamic = this.go.getBehaviour(DynamicObject);
    this.isAtBaseColor = true;
  }

  override fixedUpdate() {
    const handler = this.handler();
    const next = handler && this.go.position.x < handler.go.position.x ? -1 : 1;
    if (next !== this.ballSide && this.state === VolleyballState.Serving) this.state = VolleyballState.Volleying;
    this.ballSide = next;
    this.ballOnFloorCounter -= 1;
    if (this.ballOnFloorCounter < 0) this.ballOnFloorCounter = 0;
  }

  /** In the air and already coloured: the point is live, so pause waits. */
  canPause(_player: GamePlayer): boolean {
    return this.ballOnFloorCounter > 0 || this.isAtBaseColor;
  }

  override onCollisionEnter(c: Collision2D) {
    const player = playerOf(c.collider) ?? playerOf(c.gameObject);
    if (player) {
      if (this.state === VolleyballState.Start) this.state = VolleyballState.Volleying;
      this.changeColor(player.color);
      this.sound('volleyHit');
    }
    if (this.isFloor(c)) this.landed();
  }

  override onCollisionStay(c: Collision2D) {
    if (this.isFloor(c)) this.ballOnFloorCounter = 2;
  }

  tint(): [number, number, number, number] | undefined {
    const g = this.dynamic?.matchPlayerColorSprites[0];
    const c = g?.sprite?.color;
    if (!c) return undefined;
    return [c[0], c[1], c[2], c[3]];
  }

  applyTint(color: [number, number, number, number]) {
    this.isAtBaseColor = color[0] === this.baseColor[0] && color[1] === this.baseColor[1] && color[2] === this.baseColor[2] && color[3] === this.baseColor[3];
    this.dynamic?.setMatchPlayerColor(color, false);
  }

  private landed() {
    this.ballOnFloorCounter = 2;
    if (this.state !== VolleyballState.Volleying) return;
    this.sound('volleyScore');
    this.handler()?.logGoal(this.ballSide);
    this.state = VolleyballState.Serving;
    this.changeColor(this.baseColor);
  }

  private changeColor(color: RGBA) {
    this.isAtBaseColor = color[0] === this.baseColor[0] && color[1] === this.baseColor[1] && color[2] === this.baseColor[2] && color[3] === this.baseColor[3];
    this.dynamic?.setMatchPlayerColor(color);
  }

  private isFloor(c: Collision2D): boolean {
    return c.collider.go.tag === 'Floor' || c.gameObject.tag === 'Floor';
  }

  private handler(): VolleyballHandler | null {
    for (const b of this.game.behaviours) if (b instanceof VolleyballHandler) return b;
    return null;
  }
}
